import React, { useCallback, useEffect, useState } from 'react';
import { AmountInputBox } from 'pages/Landing/components/AmountInputBox';
import styled from '@emotion/styled';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { toast } from 'react-toastify';
import { userDeposit } from 'apis';
import { appActions } from 'store/app';
import { useLocation } from 'react-router-dom';
import { SOCIAL } from 'data/socials';
import {
	fetchAgentPoolUserBalance,
	getAptosIndexerBalance,
} from 'utils/getAptosBalance';
import { useAptosContractBalance } from 'hooks/useAptosContractBalance';
import { useAptosWallet } from '@razorlabs/wallet-kit';
import { CONTRACT_TOKEN, MOVE_VALUE } from 'data/constant';

const Column = styled('div')`
	display: flex;
	flex-direction: column;
`;

const SpaceBetween = styled(Column)`
	height: 100%;
	justify-content: space-between;
`;

const StyledButton = styled(Button)<{ disabled?: boolean }>`
	display: flex;
	padding: 12px 32px;
	box-shadow: 0 4px 4px transparent;
	border-radius: 8px;
	overflow: hidden;
	background-color: ${({ disabled }) => (disabled ? '#dadada' : '#541d18')};

	:hover {
		background-color: ${({ disabled }) =>
			disabled ? '#dadada' : 'rgba(65,17,15,0.47)'};
		cursor: ${({ disabled }) => (disabled ? 'no-drop' : 'pointer')};
		box-shadow: 0 4px 4px transparent;
	}
`;

const twitterSocial = SOCIAL.find((x) => x.name.toLowerCase() === 'twitter');

export const DepositPanel: React.ComponentType<{ onSuccess?(): void }> = ({
	onSuccess,
}) => {
	const [loading, setLoading] = useState(false);
	const [balance, setBalance] = useState('0');
	const [contractBal, setContractBal] = useState('0');
	const [amount, setAmount] = useState('');

	const { connected } = useAptosWallet();
	const dispatch = useDispatch();
	const location = useLocation();
	const selectedVault = useSelector((state: RootState) => state.vault);
	const selectedAsset = selectedVault.token.find(
		(d) => d.name === CONTRACT_TOKEN,
	);
	const selectedExchange = selectedVault.exchanger.find(
		(d) => d.value === selectedVault.exchange,
	);

	const queryParams = new URLSearchParams(location.search);
	const devMode = queryParams.get('devmode');
	const minAllowed = devMode ? 0.1 : selectedAsset?.minAllowed || 0;
	const { aptosAccount } = useAptosContractBalance();
	const { signAndSubmitTransaction } = useAptosContractBalance();

	const isDisabled =
		parseFloat(amount || '0') >= (selectedAsset?.maxAllowed || 10000) ||
		parseFloat(amount || '0') < minAllowed;
	const disabledMessage =
		Number(amount) < minAllowed
			? `Min ${minAllowed} ${CONTRACT_TOKEN} allowed`
			: `${minAllowed}-${
					selectedAsset?.maxAllowed || 10000
			  } Currently in beta testing`;

	const formattedBalance = Number(balance).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
	const formattedContractBal = Number(contractBal).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

	const getAptosAccountBalance = useCallback(async () => {
		const res = await getAptosIndexerBalance(aptosAccount?.address || '');
		setBalance(res);
	}, [aptosAccount?.address]);

	const getAptosContractBalance = useCallback(async () => {
		const res = await fetchAgentPoolUserBalance(
			selectedExchange?.contractAddress || '',
			aptosAccount?.address || '',
		);
		setContractBal(res);
	}, [aptosAccount?.address, selectedExchange?.contractAddress]);

	const onAptosDeposit = async () => {
		if (parseFloat(amount) > parseFloat(balance)) {
			return toast(
				'Wallet does not have sufficient funds to deposit.' || {
					type: 'error',
					icon: false,
					style: { backgroundColor: '#fff' },
					progressStyle: { background: '#e53838' },
				},
			);
		}

		try {
			setLoading(true);
			if (selectedExchange && selectedExchange.contractAddress) {
				const res = await signAndSubmitTransaction({
					payload: {
						function: `${selectedExchange?.contractAddress}::agent_pool::deposit`,
						typeArguments: [
							'0x1::aptos_coin::AptosCoin',
							//'0xa', //'0xa::aptos_coin::AptosCoin',
						],
						functionArguments: [
							(parseFloat(amount) * MOVE_VALUE)
								.toFixed(0)
								.toString(),
							aptosAccount?.address,
						],
					},
				});

				await new Promise((resolve) => setTimeout(resolve, 8000));

				if (res.status === 'Approved') {
					await userDeposit({
						contract: selectedExchange.contractAddress || '',
						address: aptosAccount?.address || '',
						amount,
						token: CONTRACT_TOKEN,
						eventType: 'deposit',
					});
					toast(
						'Successfully staked into vault contract' || {
							type: 'success',
						},
					);
					setBalance('0');
					await getAptosContractBalance();
					await getAptosAccountBalance();
					onSuccess?.();
				}
			}
		} catch (error) {
			toast(
				'Something is not right' || {
					type: 'error',
				},
			);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getAptosContractBalance();
		getAptosAccountBalance();
	}, [getAptosAccountBalance, getAptosContractBalance]);

	return (
		<SpaceBetween>
			<Column>
				<AmountInputBox
					currency={{
						icon: selectedVault.token[0].icon,
						name: selectedVault.token[0].name,
					}}
					text={amount}
					onChange={setAmount}
					leftTxt={`${selectedVault.currency} ${formattedBalance}`}
					rightTxt={`${selectedVault.currency} ${formattedContractBal}`}
				/>
				<Box height={20} />
				<StyledButton
					onClick={() => {
						if (!connected) {
							dispatch(appActions.openDrawer(true));
						} else if (selectedExchange?.depositDisabled) {
							toast('Service is currently unavailable.', {
								type: 'default',
							});
						} else {
							onAptosDeposit();
						}
					}}
					disabled={
						true ||
						loading ||
						(connected && parseFloat(amount || '0') === 0) ||
						(connected && isDisabled)
					}
					sx={{ height: 52 }}
					fullWidth
				>
					<Typography
						variant={'button'}
						fontWeight="bold"
						py="6px"
						color={'#fff'}
						style={{ lineHeight: '120%' }}
					>
						{!connected
							? 'Connect'
							: isDisabled
							? disabledMessage
							: loading
							? 'Please wait...'
							: 'Deposit'}
					</Typography>
				</StyledButton>
				<br />
				<StyledButton
					onClick={() => {
						window.open(twitterSocial?.url, '_blank');
					}}
				>
					<Typography
						variant={'button'}
						fontWeight="bold"
						py="6px"
						color={'#fff'}
						style={{ lineHeight: '120%' }}
					>
						Engage with {selectedVault.name}
					</Typography>
				</StyledButton>
			</Column>
		</SpaceBetween>
	);
};
